import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { IAxiosCacheAdapterOptions, ISetupCache, setupCache } from 'axios-cache-adapter'
import { registerInterceptor } from 'axios-cached-dns-resolve'

import { COOKIES, CATALOG_ENDPOINTS, PERFORMERS_NAVIGATION_ENDPOINT } from '@/constants'
import { _anonymousId, _userid } from '@/utils/analytics/segment'
import { getCookie } from '@/utils/cookies'

const isServer = typeof window === 'undefined'

type AxiosInstanceType = AxiosInstance & { cache: ISetupCache }

const apiFactory = (
    baseURL: string,
    headers: Record<string, unknown> = {},
    options: IAxiosCacheAdapterOptions = {},
): AxiosInstanceType => {
    const cache = isServer
        ? setupCache({
              maxAge: 0,
              ...options,
              exclude: {
                  query: false,
                  filter: (reqInfo: { url: string }) => {
                      return reqInfo.url !== PERFORMERS_NAVIGATION_ENDPOINT
                  },
                  ...options.exclude,
              },
          })
        : undefined

    const dnsConfig = {
        disabled: process.env.AXIOS_DNS_DISABLE === 'true',
        dnsTtlMs: 5,
        cacheGraceExpireMultiplier: 2,
        backgroundScanMs: 2400,
        dnsCacheSize: 100,
    }

    const axiosInstance = axios.create({
        // Add caching for server-side queries only - will be stored in memory
        adapter: cache?.adapter,
        baseURL,
        headers: { Accept: 'application/json', ...headers },
        timeout: 10000, // 10 seconds
        ...dnsConfig,
    })

    // Append cache controller to the instance for direct access if needed
    return { ...axiosInstance, cache } as AxiosInstanceType
}

export const hermesBaseUrl = isServer ? process.env.HERMES_SERVICE : process.env.NEXT_PUBLIC_HERMES_SERVICE
const legacyWebUrl = process.env.INTERNAL_LEGACY_WEB_URL || '' // server side only
const optimizelyDatafileUrl = process.env.NEXT_PUBLIC_OPTIMIZELY_FULLSTACK_DATAFILE || '' // server side only

/* Add new services here */
if (!hermesBaseUrl) {
    throw new Error(`Missing ${isServer ? 'HERMES_SERVICE' : 'NEXT_PUBLIC_HERMES_SERVICE'} environment variable`)
}

export const legacyWebService = apiFactory(legacyWebUrl)
export const hermesService = apiFactory(hermesBaseUrl)
export const optimizelyService = apiFactory(
    optimizelyDatafileUrl,
    {},
    {
        maxAge: process.env.NEXT_PUBLIC_SENTRY_ENV === 'production' ? 5 * 60 * 1000 : 0,
        exclude: { filter: () => false },
    }, // cache for 5 minutes on production, disable filter
)

export const errorInterceptor = (err: string & Error) => {
    if (typeof window === 'undefined') {
        const { appLogger } = require('../../log')
        appLogger.error(err?.message || err)
    }
    throw err
}

const setSearchAnalyticsToken = (config: AxiosRequestConfig) => {
    let userId = undefined

    if (!!window.analytics.user?.()) {
        userId = _userid() || _anonymousId()
    } else {
        // falling back to cookie since for the first load we might not have analytics.user yet
        const anonymousId = getCookie(document.cookie, COOKIES.SEGMENT_ANONYMOUS_USER)
        const loggedInUser = getCookie(document.cookie, COOKIES.SEGMENT_LOGGEDIN_USER)

        userId = loggedInUser || anonymousId
    }

    if (!!CATALOG_ENDPOINTS.find((endpoint) => endpoint === config.url)) {
        config.params = userId ? { ...config.params, searchAnalyticsUserToken: userId } : config.params
    }
}

const requestInterceptor = (config: AxiosRequestConfig): AxiosRequestConfig => {
    if (!isServer) {
        const sendAnalyticsUserToken = getCookie(document.cookie, COOKIES.SEND_SEARCH_ANALYTICS_TOKEN)

        if (sendAnalyticsUserToken) {
            setSearchAnalyticsToken(config)
        }
    }
    /*
        WARNING!
        FEATURE BRANCH ONLY PROXY PATCH
        https://vividseats.atlassian.net/browse/BW-353
    */
    if (process.env.VIVID_ROUTE_ENABLED === 'true') {
        if (typeof window === 'undefined') {
            // server side we get this from vivid_route
            config.headers['x-request-route'] = process.env.VIVID_ROUTE
        } else {
            // client side we get this from the cookie
            config.headers['x-request-route'] = getCookie(document.cookie, 'x-request-route')
        }

        // if the header doesn't have a value, delete it
        if (!config.headers['x-request-route']) {
            delete config.headers['x-request-route']
        }
    }

    return config
}

hermesService.interceptors.request.use(requestInterceptor)
hermesService.interceptors.response.use((res) => res, errorInterceptor)
legacyWebService.interceptors.response.use((res) => res, errorInterceptor)

// on the server-side - cache dns entries
if (isServer) {
    // dns interceptor
    registerInterceptor(hermesService)
    registerInterceptor(legacyWebService)
    registerInterceptor(optimizelyService)
}

export default apiFactory
